import { useContext, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { deleteRequest, getRequest } from '../../../services/base'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import LinkWithStatus from '../../common/LinkWithStatus'
import {
  CircuitContext,
  SET_CIRCUIT_DATA,
  SET_SELECTED_CIRCUIT_DATA,
  SET_SHOW_HIDE_CIRCUIT_POPUP,
} from './context/context'
import CircuitRow from './CircuitRow'
import { NavContext } from '../../../context/global_context'
import { getVehicleRedirectUrl, toTitleCase } from '../../../utils/common'
import NoRecordsFound from '../../common/NoRecordsFound'
import Pagination from '../../common/Pagination'
import CircuitForm from './form'
import Select from 'react-select'

const Circuit = () => {
  const { vehicle } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchValue, setSearchValue] = useState('')
  const [selectedSignalName, setSelectedSignalName] = useState('')
  const [signalOptions, setSignalOptions] = useState([])

  const currentUrl = useRef(
    `/api/circuit/?vehicle=${vehicle}&search=${searchValue}&signal_name=${selectedSignalName}`
  )
  const [state, dispatch] = useContext(CircuitContext)
  const [navState, navDispatch] = useContext(NavContext)

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: 'Admin Tools',
        sub_header: `Vehicle List / ${vehicle} / Circuit`,
        id: '',
        show_back_btn: true,
        dtc_details: {},
        back_btn_url: getVehicleRedirectUrl(),
        full_width_container: false,
      },
    })

    currentUrl.current = `/api/circuit/?vehicle=${vehicle}&search=${searchParams.get('search_value') ? searchParams.get('search_value') : ''}&signal_name=${searchParams.get('signal') ? searchParams.get('signal') : ''}`
    setSearchValue(searchParams.get('search_value'))
    setSelectedSignalName({
      value: searchParams.get('signal'),
      label: toTitleCase(
        searchParams.get('signal') ? searchParams.get('signal') : ''
      ),
    })
    getCircuitListData()
    getSignalList('')
  }, [])

  const getCircuitListData = async (showLoader = true) => {
    if (showLoader) {
      navDispatch({
        type: 'loader',
        data: {
          caption: 'Fetching Circuit`s Data',
          showLoader: true,
        },
        notification: {},
      })
    }

    let res = await getRequest(currentUrl.current)
    dispatch({ type: SET_CIRCUIT_DATA, data: res })
    if (showLoader) {
      navDispatch({
        type: 'loader',
        data: {},
        notification: {},
      })
    }
  }

  const deleteCircuit = async (circuit) => {
    if (
      window.confirm(
        `Are you sure want to delete ${circuit.name}, This will delete related data too.`
      ) === true
    ) {
      let res = await deleteRequest(
        `/api/circuit/${circuit.id}/?vehicle=${vehicle}`
      )
      if (res?.status) {
        onDataSave(true, `${circuit.name} Successfully Deleted!`)
      } else {
        onDataSave(false, `Something went wrong!`)
      }
      getCircuitListData(false)
    }
  }

  const getSignalList = async () => {
    let res = await getRequest(`/api/signal/options/?vehicle=${vehicle}`)
    setSignalOptions(res.data)
  }

  const onDataSave = (status, msg) => {
    if (status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: msg,
        },
      })
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: msg,
        },
      })
    }
  }

  return (
    <div className="w-full">
      {state?.showCircuitFormPopup && (
        <CircuitForm
          onDataSave={(status, msg) => {
            onDataSave(status, msg)
            getCircuitListData(false)
          }}
        />
      )}
      <form
        className="flex flex-col w-full space-y-2 pb-2"
        acceptCharset="UTF-8"
      >
        <div
          className={`flex space-x-2 content-center items-center justify-between`}
        >
          <div className="flex items-center w-full justify-between">
            <div className="flex items-center justify-between w-full pr-2">
              <InputText
                name="search_value"
                placeholder={`Search Name, Description`}
                className={`w-100`}
                value={searchValue}
                isFullSize={true}
                type="search"
                onChange={(value) => {
                  setSearchValue(value)
                }}
                isRequired={false}
                wrapperClass={`pl-0`}
              />
              <Select
                classNamePrefix="dtcs-filter"
                isMulti={false}
                value={selectedSignalName?.label ? selectedSignalName : null}
                options={signalOptions.map((val) => ({
                  value: val,
                  label: toTitleCase(val),
                }))}
                placeholder="Select Signal"
                className="w-1/2 cursor-pointer"
                onChange={(value) => {
                  setSelectedSignalName(value)
                }}
                isClearable={true}
                name="signal"
              />
            </div>

            <Button
              className={`cursor-pointer btn my-4 justify-end`}
              text={`Search`}
              onClick={() => {}}
            />
          </div>
          <div className="w-6/12 flex font-light text-center space-x-5 justify-end content-center items-center">
            <LinkWithStatus
              href={`/administration/circuit/new`}
              className={
                'cursor-pointer inline-block bg-tracer-lt-blue font-light hover:bg-blue-700 text-white py-2 px-3 rounded'
              }
              title="Create New Circuit"
              onClickFunc={() => {
                dispatch({
                  type: SET_SHOW_HIDE_CIRCUIT_POPUP,
                  data: true,
                })
              }}
            />
          </div>
        </div>
      </form>

      <div className="font-light">
        <div className="flex flex-row w-full bg-tracer-lt-blue rounded-t-lg text-white px-3 text-sm">
          <div className="flex w-3/12 items-center">Name</div>
          <div className="flex w-4/12 pl-6 items-center">Description</div>
          <div className="w-3/12 pl-6">Signal Name</div>
          <div className="w-2/12 pl-6"></div>
        </div>
      </div>

      {state?.data?.results?.map((circuit, index) => {
        return (
          <CircuitRow
            {...circuit}
            onDelete={() => {
              deleteCircuit(circuit)
            }}
            key={`circuit-${index}`}
            onShowDetails={() => {
              dispatch({
                type: SET_SELECTED_CIRCUIT_DATA,
                data: { ...circuit, index: index },
              })
            }}
          />
        )
      })}

      {state?.data?.results?.length < 1 && (
        <NoRecordsFound caption={'Circuit`s Data not found'} loading={false} />
      )}

      <div className="py-6">
        <Pagination
          context={state?.data?.pagination}
          onClickNext={(e) => {
            currentUrl.current = e
            getCircuitListData()
          }}
          onClickPrev={(e) => {
            currentUrl.current = e
            getCircuitListData()
          }}
        />
      </div>
    </div>
  )
}

export default Circuit
